import type { UseFormRegisterReturn } from "react-hook-form";
import { Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import type { MountReadStatus } from "@/hooks/useMountRead";
import type { VendorResponse } from "@models/vendor";

interface PromotionVendorFieldProps {
    id: string;
    vendors: VendorResponse[];
    /** The modal's own read of GET /vendors. */
    status: MountReadStatus;
    /** The vendor the promotion has now (edit), or null. */
    storedVendorId: number | null;
    registration: UseFormRegisterReturn;
    onRetry: () => void;
}

// The optional vendor a promotion belongs to (DecisionLog §4.14). The list is
// GET /vendors, active vendors only. Until it has loaded the field offers
// nothing, rather than only "None": a house-only list would let a vendor
// promotion be saved as a house one by mistake. The select mounts only once
// the list is in, so the form's value is shown on an option that exists.
export const PromotionVendorField = ({
    id,
    vendors,
    status,
    storedVendorId,
    registration,
    onRetry,
}: PromotionVendorFieldProps) => {
    const selectClass =
        "select select-bordered w-full bg-white text-neutral";

    if (status === "failed") {
        return (
            <div>
                <span className="font-semibold block mb-1">Vendor</span>
                <div
                    role="alert"
                    className="flex items-center justify-between gap-2 rounded border border-error/40 p-2 text-sm"
                >
                    <span>
                        Couldn't load the vendors, so a vendor can't be chosen
                        yet.
                    </span>
                    <Button
                        color={ComponentColor.Neutral}
                        size={ComponentSize.SM}
                        onClick={onRetry}
                    >
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    if (status === "loading") {
        return (
            <div>
                <label htmlFor={id} className="font-semibold block mb-1">
                    Vendor
                </label>
                <select id={id} className={selectClass} disabled>
                    <option>Loading vendors…</option>
                </select>
            </div>
        );
    }

    // A promotion may keep a vendor that has since been deactivated (the API
    // allows it, and the banner simply stops showing), but GET /vendors no
    // longer lists it. It is offered by id so an edit does not silently move
    // the promotion to "None".
    const storedIsListed =
        storedVendorId === null ||
        vendors.some((vendor) => vendor.id === storedVendorId);
    // GET /vendors has no order (review F3): by name, so the list reads the
    // same on every load. The unlisted stored vendor has no name to sort by,
    // so it stays where it is, right after the house choice.
    const byName = [...vendors].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div>
            <label htmlFor={id} className="font-semibold block mb-1">
                Vendor
            </label>
            <select id={id} className={selectClass} {...registration}>
                <option value="">None (house promotion)</option>
                {!storedIsListed && (
                    <option value={String(storedVendorId)}>
                        Vendor #{storedVendorId} (inactive)
                    </option>
                )}
                {byName.map((vendor) => (
                    <option key={vendor.id} value={String(vendor.id)}>
                        {vendor.name}
                    </option>
                ))}
            </select>
        </div>
    );
};
